import cookie from "react-cookies";
import { useMCPAuth } from "mcp/hooks/useMCPAuth";

/**
 * Parses the VITE_CINDRA_ROLES environment variable into an array of allowed roles.
 *
 * @returns Array of allowed roles (lowercase): ["coach", "facilitator", "staff"], or empty array if not set
 */
const parseAllowedRoles = (): string[] => {
    const rolesEnv = import.meta.env.VITE_CINDRA_ROLES;

    if (!rolesEnv || typeof rolesEnv !== "string" || rolesEnv.trim() === "") {
        return [];
    }

    // Split by comma, trim whitespace, convert to lowercase, filter valid roles
    const validRoles = ["coach", "facilitator", "staff"];
    return rolesEnv
        .split(",")
        .map(role => role.trim().toLowerCase())
        .filter(role => validRoles.includes(role));
};

/**
 * Checks if the current user's role matches any of the allowed roles.
 *
 * @returns true if user role matches an allowed role, false otherwise
 */
const checkUserRole = (): boolean => {
    const allowedRoles = parseAllowedRoles();

    // If no roles specified in env var, deny access
    if (allowedRoles.length === 0) {
        return false;
    }

    // Get user role from cookie
    const userRole = cookie.load("role");
    if (!userRole || typeof userRole !== "string") {
        return false;
    }

    // Check if user role matches any allowed role (case-insensitive)
    return allowedRoles.includes(userRole.toLowerCase());
};

/**
 * Custom hook to check if Cindra is enabled at both deployment and service levels,
 * and if the current user has an authorized role.
 *
 * This implements a layered check:
 * 1. Deployment level: VITE_CINDRA_ENABLED environment variable
 *    - If false, Cindra is disabled at deployment level (returns false immediately)
 *    - If true, proceeds to role check
 *
 * 2. Role-based access: VITE_CINDRA_ROLES environment variable
 *    - Comma-separated list of allowed roles: "coach", "facilitator", "staff"
 *    - If empty or not set, no roles have access (returns false)
 *    - User role is read from "role" cookie
 *    - If user role doesn't match, returns false
 *
 * 3. Service level: MCP authentication status
 *    - Requires authentication and network access
 *    - Only checked if deployment level is enabled and user role is authorized
 *
 * @returns {boolean} true if Cindra is enabled at all three levels
 */
export const useCindraEnabled = (): boolean => {
    // IMPORTANT: Always call useMCPAuth unconditionally to comply with Rules of Hooks
    // (hooks must be called in the same order on every render)
    const { isCindraEnabled: serviceEnabled } = useMCPAuth();

    // Layer 1: Check deployment-level enablement (fast, no dependencies)
    const deploymentEnabled = import.meta.env.VITE_CINDRA_ENABLED === "true";

    // If deployment level is disabled, return false immediately
    if (!deploymentEnabled) {
        return false;
    }

    // Layer 2: Check role-based access
    const roleAuthorized = checkUserRole();

    // If user role is not authorized, return false
    if (!roleAuthorized) {
        return false;
    }

    // Layer 3: Service-level enablement (already fetched unconditionally above)
    return serviceEnabled;
};
